import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";

import api from "libs/api";

const WebmasterSettingsContext = createContext();

const WebmasterSettingsProvider = ({ children }) => {
  const { t } = useTranslation();

  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { getUserData } = useApp();

  const [photo_progress, setPhotoProgress] = useState("0%");

  const {
    error,
    isLoading: loading,
    data,
  } = useQuery({
    queryKey: ["webmaster-settings", id],
    queryFn: () => api.get(`/admin/webmasters/${id}/settings`),
  });

  useEffect(() => {
    if (error) {
      navigate("/webmasters");
      toast.error(t("Webmaster not found."));
    }
  }, [error, navigate, t]);

  const updateSettings = (values, successMessage) =>
    new Promise(async (resolve, reject) => {
      toast.dismiss();

      try {
        let data_to_send = { ...values };

        if (data_to_send?.settings?.email_notifications) {
          data_to_send = {
            settings: {
              email_notifications: {
                ...data?.settings?.email_notifications,
                ...values?.settings?.email_notifications,
              },
            },
          };
        }

        await api.put(`/admin/webmasters/${id}/settings`, data_to_send);

        if (values?.type !== "webmaster") {
          queryClient.invalidateQueries(["webmaster-settings", id]);
        } else {
          queryClient.setQueryData(["webmaster-settings", id], (prev) => ({
            ...prev,
            ...data_to_send,
          }));
        }

        toast.success(successMessage || t("Saved successfully."));
        resolve();
      } catch (err) {
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const changePassword = (values) =>
    new Promise(async (resolve, reject) => {
      toast.dismiss();

      try {
        await api.put(`/admin/clients/${id}/change-password`, values);

        queryClient.setQueryData(["webmaster-settings", id], (prev) => ({
          ...prev,
          last_password_changed: new Date(),
        }));

        toast.success(t("Saved successfully."));
        resolve();
      } catch (err) {
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const updatePhoto = (file, photo_preview) => {
    setPhotoProgress("0%");

    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("photo", file);

        await axios.put(
          `${process.env.REACT_APP_API}/admin/webmasters/${id}/settings`,
          formData,
          {
            withCredentials: true,
            onUploadProgress: ({ loaded, total }) => {
              const percent = Math.floor((loaded * 100) / total);
              setPhotoProgress(`${percent}%`);
            },
          },
        );

        queryClient.setQueryData(["webmaster-settings", id], (prev) => ({
          ...prev,
          photo: photo_preview,
        }));

        resolve();
        setPhotoProgress("100%");
      } catch (err) {
        toast.error(t("Something went wrong."));
        reject(err);
      }
    });
  };

  const deleteAccount = () =>
    new Promise(async (resolve, reject) => {
      try {
        await api.delete(`/admin/webmasters/${id}`);
        await getUserData();

        navigate("/webmasters");

        toast.success(t("Deleted successfully."));
        resolve();
      } catch (err) {
        toast.error(t("Something went wrong."));
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data,
      loading,
      error,
      photo_progress,
      updateSettings,
      changePassword,
      updatePhoto,
      deleteAccount,
    };
    // eslint-disable-next-line
  }, [data, photo_progress, loading, error]);

  return (
    <WebmasterSettingsContext.Provider value={value}>
      {children}
    </WebmasterSettingsContext.Provider>
  );
};

const useWebmasterSettings = () => useContext(WebmasterSettingsContext);
export { WebmasterSettingsContext, useWebmasterSettings };
export default WebmasterSettingsProvider;
